<template>
  <q-page class="reset-page">
    <div v-if="pageLoading" class="sw-c-loader-overlay">
      <q-spinner-ios class="sw-c-loader" color="white" size="10px" />
    </div>

    <div v-if="!pageLoading && !tokenExist" class="fixed-center text-center">
      <p class="text-faded">{{ $t('login.pageExpired') }} ...</p>
      <q-btn color="primary" style="width: 130px" @click="goToLogin">
        <q-icon name="arrow_back" color="white" /> {{ $t('login.goBack') }}
      </q-btn>
    </div>

    <div v-else-if="!pageLoading" class="reset-content" ref="top">
      <template v-if="!passwordUpdated">
        <div class="reset-heading">
          <div class="reset-icon">
            <q-icon name="verified_user" size="26px" />
          </div>
          <h1 class="reset-title">{{ $t('login.resetPasswordTitle') }}</h1>
          <p class="reset-subtitle">{{ $t('login.resetPasswordSubtitle') }}</p>
        </div>

        <form class="reset-form" @submit.prevent="submit">
          <div class="reset-field">
            <label class="reset-label" for="reset-password">
              {{ $t('login.password') }}
            </label>
            <q-input
              id="reset-password"
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('login.passwordPlaceholder')"
              autocomplete="new-password"
              outlined
              hide-bottom-space
              data-cy="reset-password"
              class="reset-input"
              @keyup.enter="submit"
            >
              <template #append>
                <q-icon
                  :name="showPassword ? 'visibility_off' : 'visibility'"
                  class="password-toggle"
                  @click="showPassword = !showPassword"
                />
              </template>
            </q-input>
          </div>

          <div class="reset-field">
            <label class="reset-label" for="reset-password-confirmation">
              {{ $t('login.confirmPassword') }}
            </label>
            <q-input
              id="reset-password-confirmation"
              ref="pws"
              v-model="form.password_confirmation"
              :type="showConfirmPassword ? 'text' : 'password'"
              :placeholder="$t('login.confirmPasswordPlaceholder')"
              :rules="[
                (val) =>
                  !val ||
                  val === form.password ||
                  $t('login.errorSamePasswordValidation')
              ]"
              lazy-rules
              autocomplete="new-password"
              outlined
              data-cy="reset-password-confirmation"
              class="reset-input"
              @keyup.enter="submit"
            >
              <template #append>
                <q-icon
                  :name="showConfirmPassword ? 'visibility_off' : 'visibility'"
                  class="password-toggle"
                  @click="showConfirmPassword = !showConfirmPassword"
                />
              </template>
            </q-input>
          </div>

          <div class="requirements" data-cy="password-requirements">
            <div
              v-for="requirement in passwordChecks"
              :key="requirement.label"
              class="requirement"
              :class="{ 'requirement--valid': requirement.valid }"
            >
              <q-icon
                :name="
                  requirement.valid ? 'check_circle' : 'radio_button_unchecked'
                "
                size="16px"
                class="requirement-icon"
              />
              <span class="requirement-label">{{ requirement.label }}</span>
            </div>
          </div>

          <div v-if="errorMessage" class="reset-error" data-cy="reset-error">
            <q-icon name="error_outline" size="18px" class="reset-error-icon" />
            <span>{{ errorMessage }}</span>
          </div>

          <q-btn
            type="submit"
            icon="lock"
            no-caps
            unelevated
            class="reset-button"
            data-cy="send-new-password"
            :label="$t('login.saveNewPassword')"
            :loading="loading"
            :disable="!canSubmit || loading"
          />
        </form>
      </template>

      <div v-else class="success-content" data-cy="reset-password-success">
        <div class="success-icon-outer">
          <div class="success-icon-inner">
            <q-icon name="check" size="24px" color="white" />
          </div>
        </div>
        <h1 class="reset-title">{{ $t('login.passwordSaved') }}</h1>
        <p class="reset-subtitle">{{ $t('login.passwordSavedSubtitle') }}</p>
        <q-btn
          no-caps
          unelevated
          class="reset-button"
          data-cy="go-to-login"
          :label="$t('login.connectMe')"
          @click="goToLogin"
        />
      </div>
    </div>
  </q-page>
</template>

<script>
import { checkPassword } from 'src/utils/passwordPolicy'

// NOTE: messages returned by the API that we are able to translate
const KNOWN_API_PASSWORD_ERRORS = [
  {
    pattern: /simple sequence of \d\+? characters/i,
    key: 'login.passwordSequenceError'
  }
]

export default {
  mounted() {
    this.$q.loading.hide()
  },
  data() {
    return {
      form: {
        password: '',
        password_confirmation: ''
      },
      showPassword: false,
      showConfirmPassword: false,
      tokenExist: false,
      pageLoading: false,
      loading: false,
      passwordUpdated: false,
      errorMessage: ''
    }
  },
  computed: {
    passwordChecks() {
      return checkPassword(this.form.password).map((requirement) => ({
        ...requirement,
        label: this.$t(requirement.labelKey)
      }))
    },
    allPasswordChecksValid() {
      return this.passwordChecks.every((requirement) => requirement.valid)
    },
    sameAsPassword() {
      return this.form.password === this.form.password_confirmation
    },
    canSubmit() {
      return (
        this.allPasswordChecksValid &&
        this.form.password_confirmation.length > 0 &&
        this.sameAsPassword
      )
    }
  },
  watch: {
    'form.password'() {
      this.errorMessage = ''
    },
    'form.password_confirmation'() {
      this.errorMessage = ''
    }
  },
  beforeMount() {
    this.loginWithToken()
  },
  methods: {
    async loginWithToken() {
      this.pageLoading = true
      // remove backslash to avoid decoding error
      const key = this.$route.query.key.replace(/\\n/g, '')
      const decodedKey = decodeURIComponent(escape(window.atob(key))).split(',')
      // split instead of JSON.parse as JSON.parse doesn't work

      const email = decodedKey[0].replace('{', '').split(':')[1]
      const tmpToken = decodedKey[1].replace('}', '').split(':')[1]
      await this.$SowellProxy
        .authenticateWithToken({ email: email, token: tmpToken })
        .then((response) => {
          this.tokenExist = true
          this.pageLoading = false
          this.$store.dispatch('reporter/setToken', response.data.jwt)
        })
        .catch(() => {
          this.pageLoading = false
          this.tokenExist = false
        })
    },
    goToLogin() {
      this.$router.push({ path: '/' })
    },

    extractApiError(error) {
      const errors = Array.isArray(error)
        ? error
        : error?.response?.data?.errors || error?.errors || []
      const firstError = errors[0] || error?.data || {}
      const detail = firstError.detail || firstError.title
      const status = Number(
        error?.response?.status || firstError.status || error?.status
      )
      return { detail, status }
    },
    buildErrorMessage(error) {
      const { detail, status } = this.extractApiError(error)

      if (detail) {
        const knownError = KNOWN_API_PASSWORD_ERRORS.find((entry) =>
          entry.pattern.test(detail)
        )
        if (knownError) {
          return this.$t(knownError.key)
        }
      }

      if (status === 401) {
        return this.$t('common.error.SessionExpired')
      }
      if (status >= 500) {
        return this.$t('login.serverError')
      }
      if (detail) {
        return detail
      }
      if (status >= 400) {
        return this.$t('login.passwordInvalidFormat')
      }
      return this.$t('login.serverError')
    },
    async submit() {
      this.errorMessage = ''

      if (!this.canSubmit) {
        this.$refs.pws.validate()
        this.errorMessage = this.allPasswordChecksValid
          ? this.$t('login.errorSamePasswordValidation')
          : this.$t('login.passwordInvalidFormat')
        this.$q.notify({
          message: this.errorMessage,
          type: 'warning',
          position: 'top'
        })
        return
      }

      this.loading = true
      try {
        await this.$SowellProxy.updateUserPassword(this.form.password)
        this.passwordUpdated = true
      } catch (error) {
        this.errorMessage = this.buildErrorMessage(error)
        this.$q.notify({
          message: this.errorMessage,
          type: 'warning',
          position: 'top',
          timeout: 6000
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.reset-page {
  background: white;
  display: flex;
  flex-direction: column;
}

.reset-content {
  flex: 1;
  padding: 24px 20px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.reset-heading,
.success-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}

.success-content {
  flex: 1;
  justify-content: center;
  gap: 20px;
  padding-bottom: 10%;
}

.reset-icon {
  width: 66px;
  height: 66px;
  border-radius: 9999px;
  background: rgba(64, 196, 255, 0.15);
  color: #40c4ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reset-title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3em;
  color: #152f3b;
  margin: 0;
}

.reset-subtitle {
  font-size: 15px;
  font-weight: 400;
  line-height: 1.4em;
  color: #3e484f;
  margin: 0;
}

.reset-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.reset-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.reset-label {
  font-size: 15px;
  font-weight: 600;
  color: #152f3b;
}

.reset-input :deep(.q-field__control) {
  height: 52px;
  border-radius: 10px;
}

.reset-input :deep(.q-field__native),
.reset-input :deep(.q-field__input) {
  font-size: 16px;
  color: #152f3b;
}

.reset-input :deep(input::placeholder) {
  color: #8f9da3;
  opacity: 1;
}

.reset-input :deep(.q-field__marginal) {
  height: 52px;
}

.password-toggle {
  cursor: pointer;
  color: #103140;
  font-size: 22px;
}

.requirements {
  background: #f2f4f6;
  border-radius: 8px;
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}

.requirement {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #3e484f;
}

.requirement-icon {
  color: #bbcbd2;
  flex-shrink: 0;
}

.requirement--valid,
.requirement--valid .requirement-icon {
  color: #06d6a0;
}

.requirement-label {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2em;
}

.reset-error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: rgba(239, 71, 111, 0.1);
  border-radius: 8px;
  padding: 12px;
  color: #ef476f;
  font-size: 13px;
  line-height: 1.35em;
}

.reset-error-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.reset-button {
  width: 100%;
  height: 54px;
  background: #40c4ff;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border-radius: 15px;
}

.reset-button :deep(.q-icon) {
  font-size: 18px;
}

.success-icon-outer {
  width: 96px;
  height: 96px;
  border-radius: 9999px;
  background: rgba(6, 214, 160, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-icon-inner {
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  background: #06d6a0;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
